import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import type { RootState } from "../store";
import { publicRequest, privateRequest } from "../utils/api";

export interface EventTranslation {
  locale: string;
  title: string;
  description?: string;
  slug: string;
  detailed_description?: string;
  objectives?: string[];
  highlights?: string[];
  outcomes?: string[];
  event_report?: string;
}

export interface EventVideo {
  url: string;
  source: string;
}

export interface Event {
  id: string;
  title: string;
  description?: string;
  detailedDescription?: string;
  date: string;
  location: string;
  category: string;
  status: "upcoming" | "completed" | "ongoing";
  duration?: string;
  organizer?: string;
  attendees?: number;
  registrationLink?: string;
  objectives?: string[];
  highlights?: string[];
  outcomes?: string[];
  eventReport?: string;
  gallery?: string[];
  videos?: EventVideo[];
  starts_at?: string;
  ends_at?: string;
  created_at?: string;
  updated_at?: string;
  translations?: EventTranslation[];
}

export interface EventsResponse {
  success: boolean;
  message: string;
  data: {
    events: Event[];
    pagination: {
      current_page: number;
      last_page: number;
      per_page: number;
      total: number;
    };
  };
}

export interface UseEventsOptions {
  perPage?: number;
  page?: number;
  search?: string;
  status?: string;
  category?: string;
  locale?: string;
}

export interface EventQueryParams {
  per_page?: number;
  page?: number;
  locale?: string;
  search?: string;
  status?: string;
  category?: string;
  admin?: string;
}

export interface CreateEventData {
  starts_at: string;
  ends_at: string;
  venue?: string;
  ics_uid?: string;
  status: string;
  category?: string;
  duration?: string;
  organizer?: string;
  attendees?: number;
  registration_link?: string;
  detailed_description?: string;
  objectives?: string[];
  highlights?: string[];
  outcomes?: string[];
  event_report?: string;
  gallery?: string[];
  videos?: EventVideo[];
  translations: EventTranslation[];
}

export interface UpdateEventData extends Partial<CreateEventData> {
  id: string;
}

export interface UseEventOptions {
  admin?: boolean;
}

export const useEvents = (options: UseEventsOptions = {}) => {
  const { currentLanguage } = useSelector((state: RootState) => state.language);

  const {
    perPage = 15,
    page = 1,
    search = "",
    status = "",
    category = "",
    locale = currentLanguage,
  } = options;

  return useQuery<EventsResponse>({
    queryKey: [
      "events",
      locale,
      perPage,
      page,
      search,
      status,
      category,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        per_page: perPage.toString(),
        page: page.toString(),
        locale: locale,
        ...(search && { search }),
        ...(status && { status }),
        ...(category && { category }),
      });

      const response = await publicRequest.get(`/events?${params}`);

      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Admin hook that includes all translations
export const useEventsAdmin = (params: EventQueryParams = {}) => {
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  
  return useQuery<EventsResponse>({
    queryKey: ["events", "admin", { ...params, locale: currentLanguage }],
    queryFn: async () => {
      const queryParams = new URLSearchParams({
        per_page: (params.per_page || 15).toString(),
        page: (params.page || 1).toString(),
        locale: params.locale || currentLanguage,
        admin: "true",
        ...(params.search && { search: params.search }),
        ...(params.status && { status: params.status }),
        ...(params.category && { category: params.category }),
      });

      const response = await privateRequest.get(`/events?${queryParams}`);

      return response.data;
    },
    staleTime: 0, // Always refetch for admin
    gcTime: 5 * 60 * 1000,
  });
};

export const useEvent = (id: string | number, options: UseEventOptions = {}) => {
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  const { admin = false } = options;

  return useQuery<Event>({
    queryKey: ["event", id, currentLanguage, admin],
    queryFn: async () => {
      const params = new URLSearchParams({
        locale: currentLanguage,
        ...(admin && { admin: "true" }),
      });

      // Use privateRequest for admin, publicRequest for public
      const request = admin ? privateRequest : publicRequest;
      const response = await request.get(`/events/${id}?${params}`);

      return response.data.data; // Extract the actual event data from the response wrapper
    },
    enabled: !!id,
    staleTime: admin ? 0 : 5 * 60 * 1000, // Always refetch for admin
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Admin CRUD operations
const createEvent = async (eventData: CreateEventData): Promise<Event> => {
  const response = await privateRequest.post("/events", eventData);
  return response.data.data;
};

const updateEvent = async ({
  id,
  eventData,
}: {
  id: string | number;
  eventData: Partial<CreateEventData>;
}): Promise<Event> => {
  const response = await privateRequest.put(`/events/${id}`, eventData);
  return response.data.data.event || response.data.data;
};

const deleteEvent = async (id: string): Promise<void> => {
  await privateRequest.delete(`/events/${id}`);
};

// Admin hooks
export const useCreateEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createEvent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
    },
  });
};

export const useUpdateEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateEvent,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      queryClient.invalidateQueries({ queryKey: ["event", data.id] });
    },
  });
};

export const useDeleteEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteEvent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
    },
  });
};
